import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { EncampyPortalLoginLauncher } from "components/Encampy/PortalLoginLauncher";
import { CredentialFieldsDisplay } from "components/PortalCredential/CredentialFieldsDisplay";
import { getRequiredStateIdTypes } from "encamp-shared/src/facility/alternateId";
import { findAlternateIds } from "encamp-shared/src/facilityAlternateId/findAlternateId";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  AlternateId,
  CohortMemberStatus,
  CohortType,
  PortalCredential,
} from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useMemo, useState } from "react";
import { CredentialVerification } from "../Fulfillment/FulfillmentDetails/CredentialVerification";
import { FieldDisplay } from "../Fulfillment/FulfillmentDetails/shared/FieldComponents";
import { useCohortDetails } from "./useCohortDetails";

// Type for cohort member with credential data
type CohortMemberWithCredential = {
  id: string;
  status: CohortMemberStatus;
  facility?: {
    id: string;
    name?: string | null;
    state?: string | null;
    portalCredential?: PortalCredential | null;
    facilityAlternateIds?: AlternateId[] | null;
  } | null;
};

interface CredentialEntry {
  credentialId: string;
  portalCredential: PortalCredential;
  facilityId: string;
  facilityName: string;
  facilityState: string;
  facilityAlternateIds: AlternateId[];
}

export function CohortCredentials() {
  const theme = useTheme();
  const alerts = useAlerts();
  const { data } = useCohortDetails();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "cohort-credentials-expanded",
    { defaultValue: true }
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const cohort = data?.cohort;
  const cohortState = cohort?.state;
  const cohortType = cohort?.type;
  const reportingYear = cohort?.reportingYear;

  // Extract unique credentials from cohort members
  const credentialEntries = useMemo(() => {
    if (!cohort?.members) return [];

    // Cast members to our type for proper typing
    const members = cohort.members as CohortMemberWithCredential[];

    // Prioritize READY members, fallback to NOT_READY
    const readyMembers = members.filter(
      (m: CohortMemberWithCredential) => m.status === CohortMemberStatus.Ready
    );
    const notReadyMembers = members.filter(
      (m: CohortMemberWithCredential) =>
        m.status === CohortMemberStatus.NotReady
    );
    const membersToCheck =
      readyMembers.length > 0 ? readyMembers : notReadyMembers;

    // For CREDENTIAL type cohorts, all members share the same credential
    // Just get the first one with valid credential data
    if (cohortType === CohortType.Credential) {
      const memberWithCredential = membersToCheck.find(
        (m: CohortMemberWithCredential) =>
          m.facility?.portalCredential?.credential?.id
      );
      if (memberWithCredential?.facility?.portalCredential) {
        return [
          {
            credentialId:
              memberWithCredential.facility.portalCredential.credential?.id ??
              "",
            portalCredential: memberWithCredential.facility.portalCredential,
            facilityId: memberWithCredential.facility.id,
            facilityName: memberWithCredential.facility.name ?? "Unknown",
            facilityState: memberWithCredential.facility.state ?? "",
            facilityAlternateIds:
              (memberWithCredential.facility
                .facilityAlternateIds as AlternateId[]) ?? [],
          },
        ];
      }
      return [];
    }

    // For other cohort types, collect unique credentials by credential ID
    const credentialMap = new Map<string, CredentialEntry>();
    for (const member of membersToCheck) {
      const facility = member.facility;
      const portalCredential = facility?.portalCredential;
      const credId = portalCredential?.credential?.id;
      if (credId && facility && portalCredential && !credentialMap.has(credId)) {
        credentialMap.set(credId, {
          credentialId: credId,
          portalCredential: portalCredential,
          facilityId: facility.id,
          facilityName: facility.name ?? "Unknown",
          facilityState: facility.state ?? "",
          facilityAlternateIds:
            (facility.facilityAlternateIds as AlternateId[]) ?? [],
        });
      }
    }

    return Array.from(credentialMap.values());
  }, [cohort?.members, cohortType]);

  const currentCredential = credentialEntries[currentIndex];
  const hasMultipleCredentials = credentialEntries.length > 1;

  // Get relevant alternate ID types for the cohort's state
  const relevantAlternateIdTypes = useMemo(() => {
    return getRequiredStateIdTypes(cohortState);
  }, [cohortState]);

  // Filter alternate IDs to only show relevant ones
  const relevantAlternateIds = useMemo(() => {
    if (
      !currentCredential?.facilityAlternateIds ||
      relevantAlternateIdTypes.length === 0
    ) {
      return [];
    }
    return findAlternateIds(
      currentCredential.facilityAlternateIds,
      relevantAlternateIdTypes
    );
  }, [currentCredential?.facilityAlternateIds, relevantAlternateIdTypes]);

  const handlePrevious = () => {
    setCurrentIndex((prev) =>
      prev > 0 ? prev - 1 : credentialEntries.length - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev < credentialEntries.length - 1 ? prev + 1 : 0
    );
  };

  // Don't render if no credentials available
  if (credentialEntries.length === 0) {
    return (
      <Stack
        direction="column"
        component={Paper}
        elevation={0}
        spacing={2}
        padding={theme.spacing(2, 3)}
      >
        <Typography variant="h6">State Portal</Typography>
        <Typography variant="body2" color="text.secondary">
          No credentials available for this cohort
        </Typography>
      </Stack>
    );
  }

  const portalCredential = currentCredential.portalCredential;
  const credential = portalCredential.credential;

  const credentialsContent = (
    <CredentialFieldsDisplay
      credential={credential}
      portalUrl={portalCredential.url}
      showCombinedInvoicing={cohortState === "WV"}
    />
  );

  const verificationContent = credential && (
    <Box sx={{ mt: 1 }}>
      <CredentialVerification
        credential={credential}
        facilityId={currentCredential.facilityId}
      />
    </Box>
  );

  const verificationStatusOnly = credential && (
    <FieldDisplay
      label="Verification Status:"
      value={prettyPrintEnumValue(credential.verificationStatus)}
      noWrap
    />
  );

  const alternateIdsContent =
    relevantAlternateIds.length > 0 ? (
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5 }}
        >
          State IDs:
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {relevantAlternateIds.map((altId) => (
            <Chip
              key={altId.id}
              label={`${altId.type}: ${altId.value}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.75rem" }}
            />
          ))}
        </Stack>
      </Box>
    ) : null;

  const actionButtons = (
    <Stack direction="row" spacing={1} data-testid="action-buttons">
      <EncampyPortalLoginLauncher
        portalCredential={portalCredential}
        facilityAlternateIds={currentCredential.facilityAlternateIds}
        reportingYear={reportingYear}
        onSuccess={(message) => alerts.success(message)}
        onError={(error) => alerts.error(error)}
        size="medium"
        disabled={!credential?.username || !credential?.password}
        disabledReason={
          !credential?.username || !credential?.password
            ? "Portal credentials are required for portal login"
            : undefined
        }
      />
    </Stack>
  );

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3)}
    >
      {/* Header with title and expand/collapse button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography variant="h6" data-testid="cohort-credentials-title">
            State Portal
          </Typography>
          <IconButton size="small">
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Navigation arrows for multiple credentials */}
        {hasMultipleCredentials && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton size="small" onClick={handlePrevious}>
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              {currentIndex + 1} of {credentialEntries.length}
            </Typography>
            <IconButton size="small" onClick={handleNext}>
              <NavigateNextIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Stack>

      {/* Facility indicator when multiple credentials */}
      {hasMultipleCredentials && (
        <Typography variant="caption" color="text.secondary">
          {currentCredential.facilityName}
        </Typography>
      )}

      {/* Show only verification status when collapsed */}
      {!isExpanded && verificationStatusOnly && (
        <Box>{verificationStatusOnly}</Box>
      )}

      <Collapse in={isExpanded}>
        <Stack spacing={2}>
          {/* Credentials content */}
          {credentialsContent}

          {/* Relevant alternate IDs */}
          {alternateIdsContent}

          {/* Verification content */}
          {verificationContent}

          {/* Action buttons at bottom */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            {actionButtons}
          </Box>
        </Stack>
      </Collapse>
    </Stack>
  );
}
